/* eslint-disable @typescript-eslint/no-deprecated -- bundled Lucide GitlabIcon until lucide-react v1 upgrade */
import { useQuery } from '@apollo/client';
import { GitHubLogoIcon } from '@radix-ui/react-icons';
import { createFileRoute } from '@tanstack/react-router';
import { BotIcon, GitlabIcon } from 'lucide-react';
import { useEffect } from 'react';

import { useGetTicketProviderInstallationsQuery } from '@/api/api-hooks';
import { RequireEntitlements } from '@/components/Auth/RequireEntitlements';
import { IntegrationRow } from '@/components/GitIntegration/IntegrationRow';
import { AzureDevOpsIcon /*, GerritIcon */ } from '@/components/Icons';
import { BitbucketIcon } from '@/components/Icons/BitbucketIcon';
import { JiraIcon } from '@/components/Icons/JiraIcon';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { GET_CONNECTED_GIT_PROVIDERS } from '@/graphql/queries';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGitHubAuthorize } from '@/hooks/useGitHubAuthorize';
import { useUser } from '@/hooks/useUser';

export const Route = createFileRoute('/$organizationName/settings/integrations/')({
  component: SecuredIntegrationsPage,
});

function SecuredIntegrationsPage() {
  return (
    <RequireEntitlements admins={['isSuperAdmin']}>
      <IntegrationsPage />
    </RequireEntitlements>
  );
}

function IntegrationsPage() {
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const { value: isAgentcoreEnabled } = useFeatureFlag(FEATURE_FLAGS.AGENTCORE);
  const { enabled: isTicketAnalysisEnabled } = useEntitlement('ticket_analysis');
  const { isStartingGitHubAuthorization, openGitHubAuthorization } = useGitHubAuthorize();
  const { data: ticketInstallations } = useGetTicketProviderInstallationsQuery({
    enabled: isTicketAnalysisEnabled,
    refetchInterval: 5000,
  });
  const hasJira = ticketInstallations?.some((i) => i.provider_kind === 'JIRA_CLOUD' && i.status === 'ACTIVE');

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Integrations`;
  }, [organizationDisplayName]);

  const {
    data: connectedProvidersData,
    loading: isLoadingGitHubProviders,
    error: gitHubProvidersError,
  } = useQuery(GET_CONNECTED_GIT_PROVIDERS, { pollInterval: 5000 });
  const hasConnectedGitHubProviders = connectedProvidersData?.connectedGitProviders.length > 0;

  return (
    <SettingsPageLayout title="Integrations" subTitle="Manage connections to external services.">
      <div className="space-y-0">
        {/* GitHub */}
        <IntegrationRow
          icon={<GitHubLogoIcon className="h-6 w-6" />}
          title="Github"
          description="Connect your Github repositories"
          onClick={openGitHubAuthorization}
          isLoading={isLoadingGitHubProviders || isStartingGitHubAuthorization}
          error={gitHubProvidersError}
          errorMessage="Failed to load GitHub connection."
          buttonContent={hasConnectedGitHubProviders ? 'Edit Permissions' : 'Configure'}
        />

        {/* GitLab Self-Managed */}
        <IntegrationRow
          icon={<GitlabIcon className="h-6 w-6" />}
          title="GitLab"
          description="Connect your GitLab repositories"
          to="/$organizationName/settings/integrations/$integrationType"
          params={{ integrationType: 'gitlab', organizationName: routeOrganizationName }}
          errorMessage="Failed to load GitLab connection."
          providerKind="GITLAB_ENTERPRISE_SELF_MANAGED"
        />

        {/* Bitbucket Cloud */}
        <IntegrationRow
          icon={<BitbucketIcon className="h-6 w-6" />}
          title="Bitbucket Cloud"
          description="Connect your Bitbucket account"
          to="/$organizationName/settings/integrations/$integrationType"
          params={{ integrationType: 'bitbucket', organizationName: routeOrganizationName }}
          errorMessage="Failed to load Bitbucket connection."
          providerKind="BITBUCKET"
        />

        {/* Bitbucket Data Center */}
        <IntegrationRow
          icon={<BitbucketIcon className="h-6 w-6" />}
          title="Bitbucket Data Center"
          description="Connect your Bitbucket Data Center instance"
          to="/$organizationName/settings/integrations/$integrationType"
          params={{ integrationType: 'bitbucket-data-center', organizationName: routeOrganizationName }}
          errorMessage="Failed to load Bitbucket Data Center connection."
          providerKind="BITBUCKET_DATA_CENTER"
        />

        {/* Azure DevOps Cloud */}
        <IntegrationRow
          icon={<AzureDevOpsIcon className="h-6 w-6" />}
          title="Azure DevOps Cloud"
          description="Connect your Azure DevOps Cloud account"
          to="/$organizationName/settings/integrations/$integrationType"
          params={{ integrationType: 'azure-devops', organizationName: routeOrganizationName }}
          errorMessage="Failed to load Azure DevOps connection."
          providerKind="AZURE_DEVOPS_CLOUD"
        />

        {/* Gerrit */}
        {/* <IntegrationRow
          icon={<GerritIcon className="h-6 w-6" />}
          title="Gerrit"
          description="Connect your Gerrit instance"
          to="/$organizationName/settings/integrations/$integrationType"
          params={{ integrationType: 'gerrit', organizationName: routeOrganizationName }}
          errorMessage="Failed to load Gerrit connection."
          providerKind="GERRIT"
        /> */}
      </div>

      {isAgentcoreEnabled && (
        <>
          <div className="mt-10">
            <div className="mx-auto flex flex-col items-start space-y-1">
              <h1 className="text-primary text-3xl font-normal">Agents</h1>
              <p className="text-muted-foreground">Manage the services that deployed agents can use.</p>
            </div>
            <div className="mt-2">
              <Separator />
            </div>
          </div>

          <div className="space-y-0">
            <IntegrationRow
              icon={<BotIcon className="h-6 w-6" />}
              title="Agent connections"
              description="Manage MCP servers and credentials for deployed agents"
              to="/$organizationName/settings/integrations/agent-connections"
              params={{ organizationName: routeOrganizationName }}
              buttonContent="Configure"
            />
          </div>
        </>
      )}

      {isTicketAnalysisEnabled && (
        <>
          <div className="mt-10">
            <div className="mx-auto flex flex-col items-start space-y-1">
              <div className="flex items-center gap-2">
                <h1 className="text-primary text-3xl font-normal">Issue Tracking</h1>
                <Badge variant="secondary" className="text-[10px] font-semibold tracking-wider uppercase">
                  Labs
                </Badge>
              </div>
              <p className="text-muted-foreground">Manage issue and project tracking tools here.</p>
            </div>
            <div className="mt-2">
              <Separator />
            </div>
          </div>

          <div className="space-y-0">
            {/* Jira Cloud */}
            <IntegrationRow
              icon={<JiraIcon className="h-6 w-6" />}
              title="Jira Cloud"
              description="Connect your Jira Cloud site"
              to="/$organizationName/settings/integrations/$integrationType"
              params={{ integrationType: 'jira-cloud', organizationName: routeOrganizationName }}
              errorMessage="Failed to load Jira Cloud connection."
              buttonContent={hasJira ? 'Configure' : 'Add Instance'}
            />
          </div>
        </>
      )}
    </SettingsPageLayout>
  );
}
